import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Transient notifications.
 *
 * Deliberately tiny: a queue, an auto-dismiss timer, and four tones. Anything
 * that needs a decision from the user belongs in a modal, not a toast.
 */
let nextId = 0

export const useToastStore = defineStore('toast', () => {
  const toasts = ref([])

  function push({ type = 'info', title, message, duration }) {
    const id = ++nextId
    // Errors stay longer — they usually carry something worth reading.
    const ttl = duration ?? (type === 'error' ? 7000 : 4000)

    toasts.value.push({ id, type, title, message, duration: ttl })
    if (ttl > 0) setTimeout(() => dismiss(id), ttl)
    return id
  }

  function dismiss(id) {
    const index = toasts.value.findIndex((toast) => toast.id === id)
    if (index !== -1) toasts.value.splice(index, 1)
  }

  function clear() {
    toasts.value = []
  }

  const success = (title, message) => push({ type: 'success', title, message })
  const error = (title, message) => push({ type: 'error', title, message })
  const warning = (title, message) => push({ type: 'warning', title, message })
  const info = (title, message) => push({ type: 'info', title, message })

  /**
   * Renders an ApiError sensibly: validation problems are already shown inline
   * on the form, so those get a short prompt rather than a wall of text.
   */
  function fromError(error_, fallback = 'Something went wrong') {
    if (error_?.isValidation) {
      return push({ type: 'error', title: 'Check the form', message: error_.message })
    }
    return push({
      type: 'error',
      title: error_?.isNetwork ? 'Connection problem' : fallback,
      message: error_?.message ?? String(error_),
    })
  }

  return { toasts, push, dismiss, clear, success, error, warning, info, fromError }
})
